// src/components/common/ButtonGroupPicker.tsx
// 通用按鈕組選擇器 - 用於 Category、Priority、Energy 等欄位

import React from 'react';
import PickerShell from './PickerShell';

interface ButtonOption<T extends string> {
  value: T;
  label: string;
  color: string;
}

interface ButtonGroupPickerProps<T extends string> {
  /** 當前選中的值 */
  currentValue?: T;

  /** 選項列表 */
  options: ButtonOption<T>[];

  /** 選擇回調 - 返回選中值或 undefined（清除） */
  onSelect: (value: T | undefined) => void;

  /** 關閉回調 */
  onClose: () => void;
}

/**
 * 獲取按鈕的樣式類別
 * 根據顏色名稱返回對應的 Tailwind 類別
 */
const getButtonStyles = (color: string, isSelected: boolean): string => {
  const colorMap: Record<string, { selected: string; unselected: string }> = {
    blue: {
      selected: 'bg-sky-50 text-sky-700 border-sky-200',
      unselected: 'bg-gray-50 text-gray-700 border-gray-200 hover:border-sky-200 hover:bg-sky-50',
    },
    yellow: {
      selected: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      unselected: 'bg-gray-50 text-gray-700 border-gray-200 hover:border-yellow-200 hover:bg-yellow-50',
    },
    green: {
      selected: 'bg-green-50 text-green-700 border-green-200',
      unselected: 'bg-gray-50 text-gray-700 border-gray-200 hover:border-green-200 hover:bg-green-50',
    },
    purple: {
      selected: 'bg-sky-50 text-sky-700 border-sky-200',
      unselected: 'bg-gray-50 text-gray-700 border-gray-200 hover:border-sky-200 hover:bg-sky-50',
    },
    pink: {
      selected: 'bg-pink-50 text-pink-700 border-pink-200',
      unselected: 'bg-gray-50 text-gray-700 border-gray-200 hover:border-pink-200 hover:bg-pink-50',
    },
    red: {
      selected: 'bg-red-50 text-red-700 border-red-200',
      unselected: 'bg-gray-50 text-gray-700 border-gray-200 hover:border-red-200 hover:bg-red-50',
    },
  };

  const styles = colorMap[color] || colorMap.blue;
  return isSelected ? styles.selected : styles.unselected;
};

/**
 * 通用按鈕組選擇器
 * 提供多個選項的按鈕組，點擊已選中的選項可清除選擇
 */
function ButtonGroupPicker<T extends string>({
  currentValue,
  options,
  onSelect,
  onClose,
}: ButtonGroupPickerProps<T>) {
  const handleOptionClick = (value: T) => {
    // 點擊已選中項目，清除選擇
    onSelect(currentValue === value ? undefined : value);
    onClose();
  };

  const handleClear = () => {
    onSelect(undefined);
    onClose();
  };

  return (
    <PickerShell onClose={onClose}>
      {/* Options */}
      <div className="px-4 py-4">
        <div className="flex flex-wrap gap-2">
          {options.map((option) => {
            const isSelected = currentValue === option.value;
            return (
              <button
                key={option.value}
                onClick={() => handleOptionClick(option.value)}
                className={`
                  px-4 py-2 text-sm font-medium rounded-xl border-2
                  transition-all duration-150
                  ${getButtonStyles(option.color, isSelected)}
                `}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Clear Button */}
      {currentValue && (
        <div className="px-4 pb-4">
          <button
            onClick={handleClear}
            className="w-full py-2.5 text-sm font-medium text-gray-500 hover:text-gray-700
                       hover:bg-gray-100 rounded-lg transition-colors"
          >
            Clear selection
          </button>
        </div>
      )}
    </PickerShell>
  );
}

export default ButtonGroupPicker;
